import { lazy } from 'react';
import { Navigate, Route } from 'react-router-dom';

import AdminGuard from '@/routes/guards/admin-guard';
import KeyedByParam from '@/routes/keyed-by-param';
import ParamRedirect from '@/routes/param-redirect';
import QueryRedirect from '@/routes/query-redirect';
import SplatRedirect from '@/routes/splat-redirect';
import { paths } from '@/shared/routes/paths';
import type { RouteHandle } from '@/shared/routes/route-handle';

/**
 * The five pages under Collection share their chrome: the heading is in the
 * page, so the last crumb stays a crumb (the same shape as Session Details)
 * and "Collection" is the way back, since these pages draw no trail of their
 * own any more.
 */
const underCollection = {
  tabSegments: [{ label: 'Collection', to: paths.admin.lists.root }, 'Admin'],
  titleInPage: true,
} as const;

const Licences = lazy(() => import('@/features/licence/licences'));
const LicenceDetails = lazy(() => import('@/features/licence/licence-detail'));
const Users = lazy(() => import('@/features/user/users'));
const Administrators = lazy(() => import('@/features/administrator/administrators'));
const AddList = lazy(() => import('@/features/list/add-list'));
const AdminListsPage = lazy(() => import('@/features/list/lists'));
const EditList = lazy(() => import('@/features/list/edit-list'));
const AdminListDetails = lazy(() => import('@/features/list/list-detail'));
const ListFeedbacks = lazy(() => import('@/features/list/list-feedbacks'));
const AdminCompanies = lazy(() => import('@/features/company/companies'));
const AdminCompanyDetails = lazy(() => import('@/features/company/company-detail'));
const AdminSchools = lazy(() => import('@/features/school/schools'));
const AdminProfiles = lazy(() => import('@/features/profile/profiles'));
const DeactivatedProfiles = lazy(() => import('@/features/profile/deactivated-profiles'));
const ProfileDetails = lazy(() => import('@/features/profile/profile-detail'));
const ApiKeys = lazy(() => import('@/features/api-key/api-keys'));
const EditApiKey = lazy(() => import('@/features/api-key/edit-api-key'));
const ProfileExport = lazy(() => import('@/features/profile/profile-export'));
const FileImport = lazy(() => import('@/features/list/file-import'));
const Enrichment = lazy(() => import('@/features/enrichment/sessions'));
const EnrichmentMetrics = lazy(() => import('@/features/enrichment/metrics'));
const SessionDetails = lazy(() => import('@/features/enrichment/session-detail'));
const ProfileCleaning = lazy(() => import('@/features/profile/profile-cleaning'));
const ExitsInvestmentsReview = lazy(() => import('@/features/profile/exits-investments-review'));
const ProfileTypes = lazy(() => import('@/features/classification/profile-types'));
const NameRules = lazy(() => import('@/features/profile/name-rules'));
const AdminFaq = lazy(() => import('@/features/faq/admin-faq'));

/**
 * Admin zone (self-guarded): the whole block sits behind <AdminGuard>, so it
 * can be inlined under the shared AppLayout alongside the client routes. Admin
 * page chunks are only downloaded once an admin navigates to /admin/*.
 *
 * `handle` carries each page's title (see RouteHandle). Every admin page ends
 * its `tabSegments` with 'Admin', so the browser tab says which zone it is in
 * while the on-screen heading stays short.
 */
export const adminRoutes = (
  <Route element={<AdminGuard />}>
    <Route
      path={paths.admin.licences.root}
      element={<Licences />}
      handle={
        { title: 'Licences', tabSegments: ['Admin'], titleInPage: true } satisfies RouteHandle
      }
    />
    {/* The standalone create page is gone: a licence is created from the index's
        New licence dialog. Old links land on the index. Static segment before :id. */}
    <Route
      path={paths.legacy.addLicence}
      element={<Navigate to={paths.admin.licences.root} replace />}
    />
    <Route
      path={paths.admin.licences.detail(':id')}
      element={<LicenceDetails />}
      handle={
        {
          title: 'Licence Details',
          // The way back to the index: the page draws no trail of its own.
          tabSegments: [{ label: 'Licences', to: paths.admin.licences.root }, 'Admin'],
          // The licence's name is the identity panel's h1 (#564), so the trail
          // keeps it at crumb weight instead of restating it as a second title.
          titleInPage: true,
        } satisfies RouteHandle
      }
    />
    <Route
      path={paths.admin.users.root}
      element={<Users />}
      handle={{ title: 'Users', tabSegments: ['Admin'], titleInPage: true } satisfies RouteHandle}
    />
    {/* One route, not three. Adding and editing an administrator are dialogs the
        index opens from its own state, like every other dialog in the app, so
        they have no URLs and nothing to declare here. */}
    <Route
      path={paths.admin.administrators.root}
      element={<Administrators />}
      handle={
        { title: 'Administrators', tabSegments: ['Admin'], titleInPage: true } satisfies RouteHandle
      }
    />
    {/* The Add and Edit user pages are gone: both are dialogs, opened from the
        Users index and the licence detail. Old links land on the index. */}
    <Route
      path={paths.legacy.addUser}
      element={<Navigate to={paths.admin.users.root} replace />}
    />
    <Route
      path={paths.legacy.editUser(':id')}
      element={<Navigate to={paths.admin.users.root} replace />}
    />
    <Route
      path={paths.admin.lists.root}
      element={<AdminListsPage />}
      handle={
        { title: 'Collection', tabSegments: ['Admin'], titleInPage: true } satisfies RouteHandle
      }
    />
    <Route
      path={paths.admin.lists.add}
      element={<AddList />}
      handle={{ title: 'New List', ...underCollection } satisfies RouteHandle}
    />
    <Route
      path={paths.admin.lists.detail(':id')}
      element={<AdminListDetails />}
      handle={{ title: 'List Details', ...underCollection } satisfies RouteHandle}
    />
    <Route
      path={paths.admin.lists.edit(':id')}
      element={<EditList />}
      handle={{ title: 'Edit List', ...underCollection } satisfies RouteHandle}
    />
    <Route
      path={paths.admin.lists.fileImport(':id')}
      element={<FileImport />}
      handle={{ title: 'File Import', ...underCollection } satisfies RouteHandle}
    />
    <Route
      path={paths.admin.lists.export(':id')}
      element={<ProfileExport />}
      handle={{ title: 'Export Profiles', ...underCollection } satisfies RouteHandle}
    />
    {/* Renamed from "Lists" (#548): bookmarks and links shared before the rename
        still say /admin/lists. The splat carries the whole sub-path across. */}
    <Route
      path={`${paths.legacy.adminLists}/*`}
      element={<SplatRedirect to={paths.admin.lists.root} />}
    />
    <Route
      path={paths.admin.companies.root}
      element={<AdminCompanies />}
      handle={
        { title: 'Companies', tabSegments: ['Admin'], titleInPage: true } satisfies RouteHandle
      }
    />
    <Route
      path={paths.admin.companies.detail(':id')}
      element={<AdminCompanyDetails />}
      handle={
        {
          title: 'Company Details',
          // The way back to the table: the page draws no trail of its own, and
          // "Companies" here really is a page, not a section.
          tabSegments: [{ label: 'Companies', to: paths.admin.companies.root }, 'Admin'],
          // The page carries its own h1 (the identity band), so the trail keeps
          // the name at crumb weight instead of restating it as a second title.
          titleInPage: true,
        } satisfies RouteHandle
      }
    />
    <Route
      path={paths.admin.schools}
      element={<AdminSchools />}
      handle={{ title: 'Schools', tabSegments: ['Admin'], titleInPage: true } satisfies RouteHandle}
    />
    <Route
      path={paths.admin.profiles.root}
      element={<AdminProfiles />}
      handle={
        { title: 'Profiles', tabSegments: ['Admin'], titleInPage: true } satisfies RouteHandle
      }
    />
    {/* Static segments outrank the ":id" route in React Router's ranking */}
    <Route
      path={paths.admin.profiles.export}
      element={<ProfileExport />}
      handle={
        { title: 'Export Profiles', tabSegments: ['Profiles', 'Admin'] } satisfies RouteHandle
      }
    />
    <Route
      path={paths.admin.profiles.deactivated}
      element={<DeactivatedProfiles />}
      handle={
        {
          title: 'Deactivated Profiles',
          tabSegments: ['Profiles', 'Admin'],
          titleInPage: true,
        } satisfies RouteHandle
      }
    />
    <Route
      path={paths.legacy.suppressedProfiles}
      element={<Navigate to={paths.admin.profiles.deactivated} replace />}
    />
    <Route
      path={paths.admin.profiles.detail(':id')}
      element={
        <KeyedByParam>
          <ProfileDetails />
        </KeyedByParam>
      }
      handle={
        {
          title: 'Profile Details',
          tabSegments: [{ label: 'Profiles', to: paths.admin.profiles.root }, 'Admin'],
          titleInPage: true,
        } satisfies RouteHandle
      }
    />
    <Route
      path={paths.admin.api.root}
      element={<ApiKeys />}
      handle={{ title: 'API Keys', tabSegments: ['Admin'] } satisfies RouteHandle}
    />
    <Route
      path={paths.admin.api.edit(':id')}
      element={<EditApiKey />}
      handle={{ title: 'Edit API Key', tabSegments: ['API Keys', 'Admin'] } satisfies RouteHandle}
    />
    <Route
      path={paths.admin.enrichment.root}
      element={<Enrichment />}
      handle={
        {
          title: 'Enrichment',
          tabSegments: ['Admin'],
          titleInPage: true,
        } satisfies RouteHandle
      }
    />
    {/* Static segment outranks the ":id" route in React Router's ranking */}
    <Route
      path={paths.admin.enrichment.metrics}
      element={<EnrichmentMetrics />}
      handle={
        {
          title: 'Metrics',
          tabSegments: [{ label: 'Enrichment', to: paths.admin.enrichment.root }, 'Admin'],
          titleInPage: true,
        } satisfies RouteHandle
      }
    />
    <Route
      path={paths.admin.enrichment.detail(':id')}
      element={<SessionDetails />}
      handle={
        {
          title: 'Session Details',
          // The only way back to the table now that the page draws no trail of
          // its own, and the ancestor here really is a page, not a section.
          tabSegments: [{ label: 'Enrichment', to: paths.admin.enrichment.root }, 'Admin'],
          titleInPage: true,
        } satisfies RouteHandle
      }
    />
    {/* Renamed from "Data Collection": pipeline notifications and emails already
        out there still link to the old URLs. Static segment first, as above. */}
    <Route
      path={paths.legacy.dataCollectionMetrics}
      element={<Navigate to={paths.admin.enrichment.metrics} replace />}
    />
    <Route
      path={paths.legacy.dataCollectionDetail(':id')}
      element={<ParamRedirect to={paths.admin.enrichment.detail} />}
    />
    <Route
      path={paths.legacy.dataCollection}
      element={<Navigate to={paths.admin.enrichment.root} replace />}
    />
    <Route
      path={paths.admin.profileCleaning}
      element={
        // The former "Exits & investments to complete" card (#754) has its own
        // page since #762: its old address, kept in bookmarks and in the back
        // history, lands there.
        <QueryRedirect
          param="type"
          value="exits_investments_to_complete"
          to={paths.admin.exitsInvestments}
        >
          <ProfileCleaning />
        </QueryRedirect>
      }
      handle={
        {
          title: 'Profile Cleaning',
          tabSegments: ['Admin'],
          titleInPage: true,
        } satisfies RouteHandle
      }
    />
    <Route
      path={paths.admin.exitsInvestments}
      element={<ExitsInvestmentsReview />}
      handle={
        {
          title: 'Exit & Investment Review',
          tabSegments: ['Admin'],
          titleInPage: true,
        } satisfies RouteHandle
      }
    />
    <Route
      path={paths.legacy.urlCemetery}
      element={<Navigate to={paths.admin.profileCleaning} replace />}
    />
    <Route
      path={paths.legacy.classification}
      element={<Navigate to={paths.admin.profileTypes} replace />}
    />
    <Route
      path={paths.admin.profileTypes}
      element={<ProfileTypes />}
      handle={
        { title: 'Profile types', tabSegments: ['Admin'], titleInPage: true } satisfies RouteHandle
      }
    />
    <Route
      path={paths.admin.listFeedbacks}
      element={<ListFeedbacks />}
      handle={
        { title: 'List Feedbacks', tabSegments: ['Admin'], titleInPage: true } satisfies RouteHandle
      }
    />
    <Route
      path={paths.admin.nameRules}
      element={<NameRules />}
      handle={
        { title: 'Name Rules', tabSegments: ['Admin'], titleInPage: true } satisfies RouteHandle
      }
    />
    <Route
      path={paths.admin.faq}
      element={<AdminFaq />}
      handle={{ title: 'FAQ', tabSegments: ['Admin'], titleInPage: true } satisfies RouteHandle}
    />
  </Route>
);
